import { useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { languageNames } from '../../i18n'
import { ChevronDown, Globe, Check } from 'lucide-react'

const LANGS = Object.entries(languageNames) as [string, { native: string; english: string }][]

/**
 * Accessible language switcher. Wired to i18next (en/hi/mr).
 * - Keyboard: Enter/Space opens, Escape closes, arrow keys move, Enter selects.
 * - Preserves current page (only changes i18n language, not the route).
 * - Updates <html lang> so screen readers + Devanagari font swap apply.
 */
export function LanguageSwitcher({ variant = 'header' }: { variant?: 'header' | 'compact' | 'bar' }) {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [focusIdx, setFocusIdx] = useState(-1)
  const btnRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const current = i18n.language?.split('-')[0] || 'en'

  const change = (lng: string) => {
    i18n.changeLanguage(lng)
    document.documentElement.lang = lng
    setOpen(false)
    setFocusIdx(-1)
  }

  // Mouse click on the trigger toggles the dropdown (keyboard handled in onKeyDown).
  const toggle = () => {
    setOpen((o) => {
      if (!o) setFocusIdx(LANGS.findIndex(([l]) => l === current))
      return !o
    })
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!listRef.current?.contains(e.target as Node) && !btnRef.current?.contains(e.target as Node)) {
        setOpen(false)
        setFocusIdx(-1)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
      e.preventDefault()
      setOpen(true)
      setFocusIdx(LANGS.findIndex(([l]) => l === current))
      return
    }
    if (e.key === 'Escape') {
      setOpen(false)
      btnRef.current?.focus()
      return
    }
    if (open) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setFocusIdx((i) => Math.min(i + 1, LANGS.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setFocusIdx((i) => Math.max(i - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (focusIdx >= 0) change(LANGS[focusIdx][0])
      }
    }
  }

  useEffect(() => {
    if (open && focusIdx >= 0) {
      const items = listRef.current?.querySelectorAll('[role="option"]')
      ;(items?.[focusIdx] as HTMLElement)?.focus()
    }
  }, [open, focusIdx])

  return (
    <div className="relative" onKeyDown={onKeyDown}>
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Change language. Current: ${languageNames[current]?.english}`}
        onClick={toggle}
        className={
          variant === 'compact'
            ? 'p-2 rounded-lg text-snow-600 hover:bg-snow-100 transition min-w-[44px] min-h-[44px]'
            : variant === 'bar'
            ? 'flex items-center gap-1 px-2 h-9 rounded-md text-xs font-semibold text-snow-700 hover:bg-warm-100 transition whitespace-nowrap'
            : 'flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold text-snow-700 bg-snow-50 border border-snow-200 hover:bg-white hover:border-[rgb(15_76_129)]/40 hover:text-[rgb(15_76_129)] transition min-h-[40px]'
        }
      >
        <Globe className={variant === 'bar' ? 'w-3.5 h-3.5' : 'w-4 h-4'} aria-hidden="true" />
        {variant === 'bar' && (
          <span className="uppercase tracking-wide">{current}</span>
        )}
        {variant === 'header' && <span>{languageNames[current]?.native}</span>}
        {variant !== 'compact' && (
          <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
        )}
      </button>
      {open && (
        <div
          ref={listRef}
          role="listbox"
          aria-label="Select language"
          className="absolute right-0 mt-2 w-44 rounded-xl bg-white shadow-xl border border-snow-200 py-1 z-50 animate-scale-in origin-top-right"
        >
          {LANGS.map(([code, info], idx) => (
            <button
              key={code}
              role="option"
              aria-selected={code === current}
              onClick={() => change(code)}
              className={`w-full flex items-center justify-between px-3 py-2.5 text-sm hover:bg-snow-50 transition ${
                code === current ? 'text-[rgb(15_76_129)] font-semibold' : 'text-snow-700'
              }`}
            >
              <span className="flex flex-col items-start">
                <span>{info.native}</span>
                <span className="text-xs text-snow-500">{info.english}</span>
              </span>
              {code === current && <Check className="w-4 h-4" aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}